/** 支払い先マスタ(V-)。一覧・登録・編集・マージ・登録依頼の処理(経理・管理者) */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import Modal from '@core/components/Modal';
import {
  createVendor,
  dismissVendorRequest,
  fetchVendorRequests,
  fetchVendors,
  mergeVendors,
  resolveVendorRequest,
  updateVendor,
} from './vendorRepo';
import type { Vendor, VendorRequest } from '@core/lib/types';

type FormState = { id: string | null; name: string; bankInfo: string; note: string; active: boolean };

export default function VendorsTab() {
  const { user } = useAuth();
  const toast = useToast();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [requests, setRequests] = useState<VendorRequest[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [mergeSource, setMergeSource] = useState<Vendor | null>(null);
  const [mergeTarget, setMergeTarget] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, r] = await Promise.all([fetchVendors(true), fetchVendorRequests(true)]);
      setVendors(v);
      setRequests(r);
    } catch (e) {
      toast(e instanceof Error ? e.message : '支払い先を取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    if (!form || !user) return;
    setBusy(true);
    try {
      if (form.id) {
        await updateVendor(form.id, form);
        toast('支払い先を更新しました');
      } else {
        await createVendor(form, user.id);
        toast('支払い先を登録しました');
      }
      setForm(null);
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function doMerge() {
    if (!mergeSource || !mergeTarget || !user) return;
    setBusy(true);
    try {
      const result = await mergeVendors({ sourceId: mergeSource.id, targetId: mergeTarget, userId: user.id });
      toast(`「${mergeSource.name}」を統合しました(取引${result.moved}件を付け替え)`);
      setMergeSource(null);
      setMergeTarget('');
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '統合に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function approveRequest(r: VendorRequest) {
    if (!user) return;
    setBusy(true);
    try {
      await resolveVendorRequest(r, user.id);
      toast(`「${r.name}」を支払い先マスタに登録しました`);
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '登録に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {requests.length > 0 && (
        <div className="card">
          <h3>登録依頼(未対応 {requests.length}件)</h3>
          <table>
            <thead>
              <tr>
                <th>名称</th>
                <th>補足</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td className="hint">{r.note ?? ''}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn-sm-ok" disabled={busy} onClick={() => void approveRequest(r)}>
                      登録する
                    </button>{' '}
                    <button
                      className="btn-sm-ng"
                      disabled={busy}
                      onClick={() => void dismissVendorRequest(r.id, user?.id ?? '').then(load)}
                    >
                      却下
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          支払い先マスタ
          <button
            className="btn-line"
            style={{ marginLeft: 'auto' }}
            onClick={() => setForm({ id: null, name: '', bankInfo: '', note: '', active: true })}
          >
            新規登録
          </button>
        </h3>
        <p className="hint" style={{ marginBottom: 12 }}>
          申請フォームはここに登録された支払い先しか選べません(自由入力禁止・表記ゆれを構造的に防ぐ)。
          自動生成などで重複ができた場合は「統合」で1つにまとめられます(取引の紐づけも付け替わります)。
        </p>

        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>管理番号</th>
                <th>名称</th>
                <th>振込先情報</th>
                <th>備考</th>
                <th>状態</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => (
                <tr key={v.id}>
                  <td className="hint">{v.code}</td>
                  <td>{v.name}</td>
                  <td className="hint">{v.bank_info ?? '—'}</td>
                  <td className="hint">{v.note ?? ''}</td>
                  <td>
                    <span className={`badge ${v.active ? 'b-ok' : 'b-ng'}`}>
                      {v.active ? '有効' : '無効'}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button
                      className="txn-link"
                      onClick={() =>
                        setForm({
                          id: v.id,
                          name: v.name,
                          bankInfo: v.bank_info ?? '',
                          note: v.note ?? '',
                          active: v.active,
                        })
                      }
                    >
                      編集
                    </button>
                    {v.active && (
                      <button className="txn-link" onClick={() => setMergeSource(v)}>
                        統合
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {form && (
        <Modal onClose={() => setForm(null)} width="min(560px, 92vw)">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <h3>{form.id ? '支払い先を編集' : '支払い先を登録'}</h3>
          </div>
          <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div>
              <label>名称</label>
              <input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label>振込先情報(任意)</label>
              <input
                value={form.bankInfo}
                onChange={(e) => setForm({ ...form, bankInfo: e.target.value })}
                placeholder="例)〇〇銀行 △△支店 普通 1234567"
              />
            </div>
            <div>
              <label>備考(任意)</label>
              <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            {form.id && (
              <div>
                <label>状態</label>
                <select
                  value={form.active ? '1' : '0'}
                  onChange={(e) => setForm({ ...form, active: e.target.value === '1' })}
                >
                  <option value="1">有効</option>
                  <option value="0">無効</option>
                </select>
              </div>
            )}
          </div>
          <button className="btn-green" onClick={() => void save()} disabled={busy}>
            {busy ? '保存中…' : '保存'}
          </button>
        </Modal>
      )}

      {mergeSource && (
        <Modal onClose={() => setMergeSource(null)} width="min(560px, 92vw)">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <h3>支払い先を統合</h3>
          </div>
          <p className="hint" style={{ marginBottom: 12 }}>
            「{mergeSource.name}({mergeSource.code})」に紐づく取引をすべて統合先へ付け替え、
            この支払い先を無効化します。元に戻す場合は手動での付け替えが必要です。
          </p>
          <label className="hint">統合先</label>
          <select
            style={{ width: '100%', padding: '9px 11px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit', marginTop: 4 }}
            value={mergeTarget}
            onChange={(e) => setMergeTarget(e.target.value)}
          >
            <option value="">選択してください</option>
            {vendors
              .filter((v) => v.active && v.id !== mergeSource.id)
              .map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}({v.code})
                </option>
              ))}
          </select>
          <button className="btn-green" onClick={() => void doMerge()} disabled={busy || !mergeTarget}>
            {busy ? '統合中…' : '統合する'}
          </button>
        </Modal>
      )}
    </>
  );
}
